import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {
    CreateSegmentFromCampaignRequest,
    CreateSegmentRequest,
    SegmentActionResponse,
    UpdateSegmentRequest
} from "../../model/dto/api/saved-segment.interface";
import {SavedSegmentDto} from "../../model/dto/entity/saved-segment.dto";
import {SegmentVersionDto} from "../../model/dto/entity/segment-version.dto";
import {SegmentUsageDto} from "../../model/dto/entity/segment-usage.dto";

/**
 * Service for SavedSegment entity operations.
 * F-017: Saved Selection Segments - Provides API access for segment management.
 */
@Injectable({
    providedIn: 'root'
})
export class SavedSegmentService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/savedSegments')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-layer-group';
    }

    /**
     * Creates a new segment.
     * F-017: Acceptance Criteria - "Given selection criteria are built, when saved as segment,
     * then segment is stored with name and description"
     */
    createSegment(request: CreateSegmentRequest): Observable<SegmentActionResponse> {
        return this.postGeneric<SegmentActionResponse>(request, '/create');
    }

    /**
     * Creates a segment from campaign selection criteria.
     */
    createSegmentFromCampaign(request: CreateSegmentFromCampaignRequest): Observable<SegmentActionResponse> {
        return this.postGeneric<SegmentActionResponse>(request, '/createFromCampaign');
    }

    /**
     * Updates a segment.
     * F-017: Acceptance Criteria - "Given a segment is updated, when used in active rolling campaigns,
     * then campaign criteria reflect the update"
     */
    updateSegment(segmentId: number, request: UpdateSegmentRequest): Observable<SegmentActionResponse> {
        const url = this.getApiUrl() + '/' + segmentId + '/update';
        return this.http.put<SegmentActionResponse>(url, request);
    }

    /**
     * Activates a segment.
     */
    activateSegment(segmentId: number): Observable<SegmentActionResponse> {
        return this.postGeneric<SegmentActionResponse>({}, '/' + segmentId + '/activate');
    }

    /**
     * Archives a segment.
     */
    archiveSegment(segmentId: number): Observable<SegmentActionResponse> {
        return this.postGeneric<SegmentActionResponse>({}, '/' + segmentId + '/archive');
    }

    /**
     * Gets all active segments.
     */
    getActiveSegments(): Observable<SavedSegmentDto[]> {
        return this.getGeneric<SavedSegmentDto[]>('/active');
    }

    /**
     * Gets segments accessible to the current user for a specific target type.
     * F-017: Acceptance Criteria - "Given a saved segment, when building campaign criteria,
     * then segments can be referenced as building blocks"
     */
    getAccessibleSegments(targetType: string): Observable<SavedSegmentDto[]> {
        return this.getGeneric<SavedSegmentDto[]>('/accessible/' + targetType);
    }

    /**
     * Gets segment version history.
     */
    getVersions(segmentId: number): Observable<SegmentVersionDto[]> {
        return this.getGeneric<SegmentVersionDto[]>('/' + segmentId + '/versions');
    }

    /**
     * Rolls back to a previous version.
     */
    rollbackToVersion(segmentId: number, versionNumber: number): Observable<SegmentActionResponse> {
        return this.postGeneric<SegmentActionResponse>({}, '/' + segmentId + '/rollback/' + versionNumber);
    }

    /**
     * Gets active usages of a segment.
     */
    getActiveUsages(segmentId: number): Observable<SegmentUsageDto[]> {
        return this.getGeneric<SegmentUsageDto[]>('/' + segmentId + '/usages');
    }

    /**
     * Records segment usage in a campaign.
     * F-017: Acceptance Criteria - "Given multiple segments, when combined with set operations,
     * then the result forms complex audience definition"
     */
    recordUsage(segmentId: number, campaignId: number, selectionCriteriaId?: number,
                ruleGroupId?: number, usageType?: string): Observable<SegmentUsageDto> {
        let url = '/' + segmentId + '/recordUsage?campaignId=' + campaignId;
        if (selectionCriteriaId) url += '&selectionCriteriaId=' + selectionCriteriaId;
        if (ruleGroupId) url += '&ruleGroupId=' + ruleGroupId;
        if (usageType) url += '&usageType=' + usageType;
        return this.postGeneric<SegmentUsageDto>({}, url);
    }

    /**
     * Removes segment usage from a rule group.
     */
    removeUsage(segmentId: number, ruleGroupId: number): Observable<void> {
        const url = this.getApiUrl() + '/' + segmentId + '/removeUsage?ruleGroupId=' + ruleGroupId;
        return this.http.delete<void>(url);
    }

    /**
     * Updates estimated count for a segment.
     */
    updateEstimatedCount(segmentId: number, count: number): Observable<SegmentActionResponse> {
        const url = this.getApiUrl() + '/' + segmentId + '/estimatedCount?count=' + count;
        return this.http.put<SegmentActionResponse>(url, {});
    }
}
